import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isNotNullOrUndefined,
  isPureArray,
  MultiSelect,
} from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';
import { MultiSelectFilterData } from '../../store/states/filterTypes';

import { FilterFormatter, mapFilterOptionToSelectData } from './formatter';
import { FILTER_DYNAMIC_WIDTH_SETTINGS } from './util';

export type MultiSelectFilterProps = {
  disabled?: boolean;
  disableDynamicWidth?: boolean;
  hideSelectAll?: boolean;
  filterData?: MultiSelectFilterData;
  entityId: string;
  filterKey: string;
  formatter: FilterFormatter;
  isFetching: boolean;
  label?: ReactNode | ReactNode[];
};

export function MultiSelectFilter({
  disabled,
  disableDynamicWidth = false,
  hideSelectAll = false,
  filterData,
  entityId,
  filterKey,
  formatter,
  isFetching,
  label,
}: MultiSelectFilterProps) {
  const { t } = useTranslation();
  const [setFilterSelectionValue, resetFilterSelections] = useStore(
    useShallow((state) => [
      state.setFilterSelectionValue,
      state.resetFilterSelections,
    ])
  );

  const [value, setValue] = useState(filterData?.selection);

  const missingOptionsError = filterData?.data && filterData?.data.length === 0;

  useEffect(() => {
    setValue(filterData?.selection);
  }, [filterData?.selection]);

  const data =
    filterData?.data
      ?.map((option) => mapFilterOptionToSelectData(option, formatter))
      .filter(isNotNullOrUndefined) ?? [];

  if (missingOptionsError) {
    console.warn(
      `There are no options available for '${filterKey}'. This is a data or configuration error.`
    );
  }

  return (
    <MultiSelect
      data={data}
      disabled={disabled || (!isFetching && data.length === 0)}
      dynamicWidthSettings={
        disableDynamicWidth ? undefined : FILTER_DYNAMIC_WIDTH_SETTINGS
      }
      inlineError={true}
      key={filterKey}
      label={label}
      title={data.length === 0 ? t('app.select.noOptionsAvailable') : undefined}
      loading={isFetching}
      maxVisibleTags={2}
      // SQL Server would run into 'maximum number of parameters per query' limits otherwise.
      // A max of 1 hides the action entirely, since it requires at least 2 visible options.
      selectAllLimits={{ max: hideSelectAll ? 1 : 500 }}
      onChange={(val) => {
        if (isPureArray(val)) {
          return setValue(val);
        }

        throw new Error('MultiSelectFilter onChange called with impure array');
      }}
      onCommit={(value) => {
        if (value.length === 0) {
          return resetFilterSelections(entityId, filterKey);
        }

        if (isPureArray(value)) {
          return setFilterSelectionValue(entityId, filterKey, value);
        }

        throw new Error('MultiSelectFilter onCommit called with impure array');
      }}
      placeholder={t('app.pageFilter.placeholder')}
      value={value?.filter(isNotNullOrUndefined) ?? []}
    />
  );
}
